import React, { useEffect, useRef, useState } from 'react';
import {
  CheckinMember,
  CheckinState,
  CheckinStep,
  CheckinTrip,
  PartyEntry,
  savePeople,
  TravelPurpose,
  TRAVEL_PURPOSES,
} from '../../../../api/stay-checkin-client';
import { StayPublicError } from '../../../../api/stays-public-client';
import { Shake, StatusLabel } from '../../../../motion';
import { ChipGroup, ToggleChip } from '../../../toggle-chip/toggle-chip';
import { BirthDateField } from '../../../shared/birth-date-field/birth-date-field';
import {
  BIRTH_PROBLEM_WORDS,
  BirthParts,
  bandOf,
  checkBirth,
  EMPTY_BIRTH,
} from '../../../shared/birth-date-field/birth-date-model';
import { ErrorText, Field, FieldError, FieldHint, Input, Label } from '../../../onboarding/onboarding.styled';
import {
  Actions,
  Ghost,
  PartyHead,
  PartyItem,
  PartyList,
  PartyName,
  Primary,
  Quiet,
  Secondary,
  StepperLabel,
} from '../../../onboarding/stay/public-stay-page.styled';
import { StayFacts } from '../../../onboarding/stay/stay-steps';
import { errorWords, StayKey, t } from '../../../onboarding/stay/stay-strings';
import { Legend, Note, Panel, PersonGrid, Section, StepHeading } from './guest-checkin.styled';

const L = 'en' as const;

interface NewGuest {
  key: number;
  first_name: string;
  last_name: string;
  birth: BirthParts;
}

export type PeopleSaved = {
  trip: CheckinTrip;
  party: CheckinMember[];
  steps: CheckinStep[];
  completed_at: string | null;
};

/* ── the draft on this device (R-ISR.4) ──
   What the guest typed and has not sent yet survives a closed tab, keyed on the
   link's last characters (never the whole token). Cleared on a good save.
   Storage can be missing (private mode): every access is guarded. */
const draftKey = (token: string) => `lbStayPeople:${token.slice(-10)}`;
type Draft = { fresh: Omit<NewGuest, 'key'>[]; purpose: TravelPurpose | null; arrival: string };
const readDraft = (token: string): Draft | null => {
  try {
    const raw = window.localStorage.getItem(draftKey(token));
    const d = raw ? (JSON.parse(raw) as Draft) : null;

    return d && Array.isArray(d.fresh) ? d : null;
  } catch {
    return null;
  }
};
const writeDraft = (token: string, d: Draft) => {
  try {
    window.localStorage.setItem(draftKey(token), JSON.stringify(d));
  } catch {
    /* no storage: the page still works, the draft just is not kept */
  }
};
const clearDraft = (token: string) => {
  try {
    window.localStorage.removeItem(draftKey(token));
  } catch {
    /* as above */
  }
};

let nextKey = 1;
const blankGuest = (): NewGuest => ({ key: nextKey++, first_name: '', last_name: '', birth: EMPTY_BIRTH });

const countWords = (adults: number, children: number) =>
  t(L, 'countLine', {
    guests: t(L, adults + children === 1 ? 'oneGuest' : 'manyGuests', { n: adults + children }),
    adults: t(L, adults === 1 ? 'oneAdult' : 'manyAdults', { n: adults }),
    children: children === 0 ? t(L, 'noChildren') : t(L, children === 1 ? 'oneChild' : 'manyChildren', { n: children }),
  });

/**
 * STEP 1 — YOUR TRIP (plan S6, rebuilt by plan ISR): the stay's facts, WHO IS
 * COMING, the purpose, the arrival time when the switch lets the guest set it,
 * the house rules in short, and S10's city-tax line.
 *
 * Who is coming: the lead is guest 1 — shown, already counted, never typed
 * again. "Add another guest" adds a person (name + Day/Month/Year). The page
 * no longer asks how many adults and children: it counts them from the dates
 * of birth as they are typed, and the server counts them again on save
 * (PUT …/checkin/people), so the counts and the people can never disagree.
 * On 2026-10-27 the owner had no row for themselves, entered themselves in a
 * row, and was counted twice with no way through (PLAN-ISR-LOG C-2, C-3).
 */
export const TripStep: React.FC<{
  token: string;
  state: CheckinState;
  onSaved: (out: PeopleSaved) => void;
  onError: (err: unknown) => string | null;
  onStale: () => void;
}> = ({ token, state, onSaved, onError, onStale }) => {
  const max = Math.max(1, state.listing.max_guests);
  const checkIn = state.stay.check_in;
  const others = state.party.filter(m => !m.is_lead).sort((a, b) => a.position - b.position);
  const draft = useRef<Draft | null>(state.trip.confirmed ? null : readDraft(token));
  const [kept, setKept] = useState<CheckinMember[]>(others.filter(o => o.age_band || o.identity_mode === 'vendor'));
  const [fresh, setFresh] = useState<NewGuest[]>(() => {
    if (draft.current) return draft.current.fresh.slice(0, max - 1).map(g => ({ ...g, key: nextKey++ }));
    if (state.trip.confirmed || others.length) return [];

    // A first visit: one empty row per person the booking named beyond the lead.
    return Array.from({ length: Math.max(0, Math.min(state.booked_party, max) - 1) }, blankGuest);
  });
  const [purpose, setPurpose] = useState<TravelPurpose | null>(
    draft.current ? draft.current.purpose : state.trip.travel_purpose
  );
  const [arrival, setArrival] = useState<string>(
    draft.current ? draft.current.arrival : state.trip.expected_arrival_time || ''
  );
  const [fields, setFields] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const askArrival = state.policy.arrival_time_setter === 'guest_may';

  // Every change is kept on this device until the save succeeds.
  useEffect(() => {
    if (busy) return;
    writeDraft(token, {
      fresh: fresh.map(g => ({ first_name: g.first_name, last_name: g.last_name, birth: g.birth })),
      purpose,
      arrival,
    });
  }, [token, fresh, purpose, arrival, busy]);

  const total = 1 + kept.length + fresh.length;
  const atMax = total >= max;
  const bands = fresh.map(g => {
    const c = checkBirth(g.birth, checkIn);

    return 'value' in c ? bandOf(c.value, checkIn) : null;
  });
  const pending = bands.some(b => b === null);
  const adults = 1 + kept.filter(k => (k.age_band || 'adult') === 'adult').length + bands.filter(b => b === 'adult').length;
  const children = kept.filter(k => k.age_band && k.age_band !== 'adult').length + bands.filter(b => b && b !== 'adult').length;

  const setGuest = (key: number, patch: Partial<NewGuest>) =>
    setFresh(list => list.map(g => (g.key === key ? { ...g, ...patch } : g)));
  const clearField = (name: string) =>
    setFields(f => {
      if (!f[name]) return f;
      const next = { ...f };
      delete next[name];

      return next;
    });

  // Every missing detail named beside its field before anything is sent.
  const localCheck = (): Record<string, string> => {
    const out: Record<string, string> = {};
    fresh.forEach((g, i) => {
      if (!g.first_name.trim()) out[`new${i}.first_name`] = 'Enter their first name.';
      if (!g.last_name.trim()) out[`new${i}.last_name`] = 'Enter their last name.';
      const c = checkBirth(g.birth, checkIn);
      if ('problem' in c) out[`new${i}.birth_date`] = BIRTH_PROBLEM_WORDS[c.problem];
    });
    if (!purpose && state.trip.purpose_required) out.travel_purpose = 'Choose the purpose of your trip.';

    return out;
  };

  const submit = async () => {
    if (busy) return;
    setProblem(null);
    const local = localCheck();
    if (Object.keys(local).length) {
      setFields(local);
      setProblem(t(L, 'fixAbove'));
      setAttempt(a => a + 1);

      return;
    }
    setFields({});
    setBusy(true);
    const guests: Array<PartyEntry | { position: number }> = [
      ...kept.map(k => ({ position: k.position })),
      ...fresh.map(g => {
        const c = checkBirth(g.birth, checkIn);

        return {
          first_name: g.first_name.trim(),
          last_name: g.last_name.trim(),
          birth_date: 'value' in c ? c.value : '',
        };
      }),
    ];
    try {
      const out = await savePeople(token, {
        version: state.trip.version,
        guests,
        travel_purpose: purpose,
        ...(askArrival ? { expected_arrival_time: arrival || null } : {}),
      });
      clearDraft(token);
      onSaved(out);
    } catch (err) {
      setBusy(false);
      setAttempt(a => a + 1);
      if (err instanceof StayPublicError && err.status === 400 && Object.keys(err.fields).length) {
        // guests[i] counts kept people first: shift the index onto the new rows.
        const shifted: Record<string, string> = {};
        for (const [k, v] of Object.entries(err.fields)) {
          const m = /^guests\[(\d+)\]\.(\w+)$/.exec(k);
          if (m) shifted[`new${Number(m[1]) - kept.length}.${m[2]}`] = v;
          else shifted[k] = v;
        }
        setFields(shifted);
        setProblem(shifted.guests || (shifted.version ? errorWords('CHECKIN_STALE') : t(L, 'fixAbove')));
        if (shifted.version) onStale();

        return;
      }
      // A refusal after the trip saved moved its version: read the state again.
      onStale();
      setProblem(onError(err));
    }
  };

  return (
    <Shake active={attempt > 0} attempt={attempt}>
      <StepHeading>{t(L, 'tripTitle')}</StepHeading>
      <Note>{t(L, 'addressLine', { building: state.listing.building_name, address: state.listing.address })}</Note>
      <StayFacts
        stay={{
          check_in: state.stay.check_in,
          check_out: state.stay.check_out,
          nights: state.stay.nights,
          check_in_time: state.listing.check_in_time,
          check_out_time: state.listing.check_out_time,
        }}
        zone={state.listing.timezone}
      />

      {state.rules_summary ? (
        <Section aria-label={t(L, 'rulesSummaryTitle')}>
          <StepperLabel>{t(L, 'rulesSummaryTitle')}</StepperLabel>
          <Panel>
            <Note>{state.rules_summary.text}</Note>
          </Panel>
        </Section>
      ) : null}

      <Section aria-labelledby="who-title" data-who="">
        <StepHeading as="h3" id="who-title">
          {t(L, 'whoTitle')}
        </StepHeading>
        <Note>{t(L, 'whoHint')}</Note>
        <PartyList>
          <PartyItem data-lead="">
            <PartyHead>
              <PartyName>{t(L, 'leadGuestRow', { first: state.guest_first_name })}</PartyName>
              <Quiet>{t(L, 'leadRowNote')}</Quiet>
            </PartyHead>
          </PartyItem>
          {kept.map((k, i) => (
            <PartyItem key={`kept-${k.position}`} data-position={k.position}>
              <PartyHead>
                <PartyName>
                  {t(L, 'guestN', { n: i + 2 })}: {k.first_name} {k.last_initial}
                </PartyName>
                <Quiet>{t(L, 'keepsAge')}</Quiet>
              </PartyHead>
              {!k.locked ? (
                <Ghost
                  type="button"
                  onClick={() => setKept(list => list.filter(x => x.position !== k.position))}
                  aria-label={t(L, 'removeName', { first: k.first_name })}
                  disabled={busy}
                >
                  {t(L, 'remove')}
                </Ghost>
              ) : null}
            </PartyItem>
          ))}
        </PartyList>

        {fresh.map((g, i) => {
          const n = kept.length + i + 2;

          return (
            <PersonGrid key={g.key} data-guest={n}>
              <Legend>{t(L, 'guestN', { n })}</Legend>
              <Field>
                <Label htmlFor={`party-${i}-first`}>{t(L, 'firstName')}</Label>
                <Input
                  id={`party-${i}-first`}
                  autoComplete="off"
                  value={g.first_name}
                  onChange={e => {
                    setGuest(g.key, { first_name: e.target.value });
                    clearField(`new${i}.first_name`);
                  }}
                  hasError={!!fields[`new${i}.first_name`]}
                  aria-invalid={fields[`new${i}.first_name`] ? true : undefined}
                  disabled={busy}
                />
                <FieldError message={fields[`new${i}.first_name`]} />
              </Field>
              <Field>
                <Label htmlFor={`party-${i}-last`}>{t(L, 'lastName')}</Label>
                <Input
                  id={`party-${i}-last`}
                  autoComplete="off"
                  value={g.last_name}
                  onChange={e => {
                    setGuest(g.key, { last_name: e.target.value });
                    clearField(`new${i}.last_name`);
                  }}
                  hasError={!!fields[`new${i}.last_name`]}
                  aria-invalid={fields[`new${i}.last_name`] ? true : undefined}
                  disabled={busy}
                />
                <FieldError message={fields[`new${i}.last_name`]} />
              </Field>
              <BirthDateField
                id={`party-${i}-dob`}
                legend={t(L, 'birthDateLabel')}
                value={g.birth}
                onChange={birth => {
                  setGuest(g.key, { birth });
                  clearField(`new${i}.birth_date`);
                }}
                hint={t(L, 'dobHint')}
                error={fields[`new${i}.birth_date`]}
                disabled={busy}
              />
              <Ghost
                type="button"
                onClick={() => {
                  setFresh(list => list.filter(x => x.key !== g.key));
                  setFields({});
                }}
                aria-label={t(L, 'removeGuest', { n })}
                disabled={busy}
              >
                {t(L, 'remove')}
              </Ghost>
            </PersonGrid>
          );
        })}

        {!atMax ? (
          <Secondary type="button" onClick={() => setFresh(list => [...list, blankGuest()])} disabled={busy}>
            {t(L, 'addAnother')}
          </Secondary>
        ) : (
          <Note role="status">{t(L, 'atMax', { max })}</Note>
        )}
        <Quiet role="status" data-count="">
          {pending ? t(L, 'countPending') : countWords(adults, children)}
        </Quiet>
        {state.booked_party !== total ? (
          <Quiet>
            {t(L, 'bookedFor', {
              n: t(L, state.booked_party === 1 ? 'oneGuest' : 'manyGuests', { n: state.booked_party }),
              max,
            })}
          </Quiet>
        ) : null}
        <FieldError message={fields.guests} />
      </Section>

      <Section>
        <StepperLabel id="trip-purpose-label">
          {state.trip.purpose_required ? t(L, 'purposeLabel') : t(L, 'purposeOptional')}
        </StepperLabel>
        <ChipGroup aria-label={state.trip.purpose_required ? t(L, 'purposeLabel') : t(L, 'purposeOptional')}>
          {TRAVEL_PURPOSES.map(p => (
            <ToggleChip
              key={p}
              pressed={purpose === p}
              onToggle={on => {
                setPurpose(on ? p : null);
                clearField('travel_purpose');
              }}
              disabled={busy}
            >
              {t(L, `purpose_${p}` as StayKey)}
            </ToggleChip>
          ))}
        </ChipGroup>
        <FieldError message={fields.travel_purpose} />
      </Section>

      {askArrival ? (
        <Field>
          <Label htmlFor="trip-arrival">{t(L, 'arrivalLabel')}</Label>
          <Input
            id="trip-arrival"
            type="time"
            value={arrival}
            onChange={e => setArrival(e.target.value)}
            aria-describedby="trip-arrival-hint"
            aria-invalid={fields.expected_arrival_time ? true : undefined}
            hasError={!!fields.expected_arrival_time}
            disabled={busy}
          />
          <FieldHint id="trip-arrival-hint">{t(L, 'arrivalHint')}</FieldHint>
          <FieldError message={fields.expected_arrival_time} />
        </Field>
      ) : null}

      {state.city_tax ? <Note data-city-tax="">{state.city_tax.text}</Note> : null}

      {problem ? <ErrorText>{problem}</ErrorText> : null}
      <Actions>
        <Primary type="button" onClick={submit} aria-busy={busy || undefined}>
          <StatusLabel state={busy ? 'busy' : 'idle'} labels={{ idle: t(L, 'continue'), busy: t(L, 'saving') }} />
        </Primary>
        <Quiet>{t(L, 'draftKept')}</Quiet>
      </Actions>
    </Shake>
  );
};
